// components/AOIToolbar.tsx
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../../../store/store";
import type { DrawingAOIMode } from "../../aoi/types";
import
{
  clearAOIFeatures,
  removeAOIFeature,
  setActiveFeature,
  setAOIEditingFeatureId,
  setDrawingAOIMode,
  toggleFeatureVisibility,
} from "../../../store/slices/aoiSlice";
import { setMeasureMode } from "../../../store/slices/measureSlice";
import { setDrawingMode } from "../../../store/slices/drawSlice";
import useToggle from "../../../../../../hooks/useToggle";
import CommonComponents from "../../../../../Commons";
import { useTranslation } from "react-i18next";

import
{
  BorderOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  GatewayOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Tooltip } from "antd";
import FormUpload from "../FormUploadAOI";
import CommonIcons from "../../../../../../assets/icons";
import { useMapContext } from "../../../core/MapContext";

interface AOIToolbarProps { }

const GREEN = "#34d399";
const EDIT_C = "#a78bfa";

const pill = (
  color: string,
  bg: string,
  border: string,
): React.CSSProperties => ({
  background: bg,
  border: `1px solid ${border}`,
  color,
  borderRadius: "3px",
  padding: "1px 5px",
  fontSize: "9px",
  fontWeight: 700,
});

const AOIToolbar: React.FC<AOIToolbarProps> = () =>
{
  const dispatch = useDispatch();

  const { mode, features, editingFeatureId, activeFeatureId } = useSelector(
    (state: RootState) => state.aoi,
  );

  const [open, setOpen] = React.useState(false);

  const {
    open: openUploadAOI,
    shouldRender: shouldRenderUploadAOI,
    toggle: toggleUploadAOI,
  } = useToggle();

  const hasData = features.length > 0;
  const isEdit = mode === "EDIT";
  const toggle = (tool: DrawingAOIMode) =>
    onSelect(mode === tool ? "NONE" : tool);

  const TOOLS: {
    type: DrawingAOIMode | "Upload_AOI";
    icon: string | React.ReactNode;
    label: string;
    key: string;
    color: string;
  }[] = [
      {
        type: "POLYGON",
        icon: <CommonIcons.PolygonDrawIcon />,
        label: "Polygon",
        key: "",
        color: GREEN,
      },
      {
        type: "RECTANGLE",
        icon: <CommonIcons.RectangleDrawIcon />,
        label: "Rectangle",
        key: "",
        color: GREEN,
      },

      {
        type: "Upload_AOI",
        icon: <CommonIcons.UploadPolygonIcon />,
        label: "Upload AOI",
        key: "",
        color: GREEN,
      },
    ];

  const TYPE_ICONS: Record<string, string> = {
    Point: "⬤",
    LineString: "╱",
    Polygon: "⬡",
  };

  //! functions
  const onSelect = (tool: DrawingAOIMode) =>
  {
    dispatch(setDrawingAOIMode(tool));
    dispatch(setDrawingMode("NONE"));
    dispatch(setMeasureMode("NONE"));
  };
  const onStopEdit = () =>
  {
    dispatch(setAOIEditingFeatureId(null));
    dispatch(setDrawingAOIMode("NONE"));
  };
  const onStartEdit = (id: string, feature: any) =>
  {
    dispatch(setAOIEditingFeatureId(id));
    dispatch(setDrawingAOIMode("EDIT"));

    if (!feature?.visible)
    {
      dispatch(toggleFeatureVisibility({ id: feature.id }));
    }


  };
  const onRemove = (id: string) =>
  {
    dispatch(removeAOIFeature(id));
  };
  const onClear = () =>
  {
    dispatch(clearAOIFeatures());
  };

  //! render
  const accentFor = (t: DrawingAOIMode | "Upload_AOI") =>
    t === "EDIT" ? EDIT_C : GREEN;

  const btnStyle = (t: DrawingAOIMode | "Upload_AOI"): React.CSSProperties =>
  {
    const ac = accentFor(t);
    const on = mode === t;
    return {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "7px 10px",
      width: "100%",
      textAlign: "left",
      background: on ? `${ac}18` : "transparent",
      border: `1px solid ${on ? `${ac}60` : "transparent"}`,
      borderRadius: "8px",
      cursor: hasData ? "not-allowed" : "pointer",
      outline: "none",
      color: on ? ac : "rgba(255,255,255,0.50)",
      fontSize: "11px",
      fontWeight: on ? 700 : 400,
      fontFamily: "inherit",
      transition: "all 0.15s",
      position: "relative",
    };
  };

  const { t } = useTranslation();

  return (
    <div
      style={{
        position: "absolute",
        top: "12px",
        right: "40px",
        zIndex: 1000,
        fontFamily: '"JetBrains Mono","Fira Code",monospace',
      }}
    >
      {/* Toggle pill */}
      <button
        onClick={() => setOpen((o) => !o)}
        title="AOI tools"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          padding: "7px 12px",
          background: "rgba(15,17,26,0.88)", //open || mode !== "NONE" ? `${GREEN}22` : "rgba(15,17,26,0.88)",
          border: `1px solid ${open || mode !== "NONE" ? `${GREEN}60` : "rgba(255,255,255,0.12)"}`,
          borderRadius: "6px",
          cursor: "pointer",
          color: open || mode !== "NONE" ? GREEN : "rgba(255,255,255,0.7)",
          fontSize: "11px",
          fontWeight: 700,
          letterSpacing: "0.5px",
          backdropFilter: "blur(8px)",
          outline: "none",
          transition: "all 0.15s",
          boxShadow: open ? `0 0 12px ${GREEN}30` : "none",
        }}
      >
        ⬡ AOI
      </button>

      {/* Panel */}
      {open && (
        <div
          style={{
            position: "absolute",
            top: "38px",
            right: "2px",
            zIndex: 999,
            width: "240px",
            background: "rgba(13,15,24,0.97)",
            backdropFilter: "blur(16px)",
            border: "1px solid rgba(255,255,255,0.10)",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 12px 48px rgba(0,0,0,0.6)",
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: "8px 12px",
              borderBottom: "1px solid rgba(255,255,255,0.07)",
              fontSize: "9px",
              fontWeight: 700,
              letterSpacing: "2px",
              color: "rgba(255,255,255,0.35)",
              textTransform: "uppercase",
            }}
          >
            AOI Tools
          </div>

          <div style={{ padding: "6px" }}>
            {/* Tool buttons */}
            {TOOLS.map((t) => (
              <button
                key={t.type}
                style={btnStyle(t.type)}
                onClick={() =>
                {
                  if (t.type !== "Upload_AOI")
                  {
                    toggle(t.type);
                  } else
                  {
                    toggleUploadAOI();
                  }
                }}
                onMouseEnter={(e) =>
                {
                  if (mode !== t.type)
                  {
                    e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                    e.currentTarget.style.color = "rgba(255,255,255,0.85)";
                  }
                }}
                onMouseLeave={(e) =>
                {
                  if (mode !== t.type)
                  {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = "rgba(255,255,255,0.50)";
                  }
                }}
                disabled={hasData}
              >
                {mode === t.type && (
                  <span
                    style={{
                      position: "absolute",
                      left: 0,
                      top: "20%",
                      bottom: "20%",
                      width: "2px",
                      background: t.color,
                      borderRadius: "0 2px 2px 0",
                    }}
                  />
                )}
                <span
                  style={{
                    fontSize: "13px",
                    minWidth: "16px",
                    textAlign: "center",
                  }}
                >
                  {t.icon}
                </span>
                <span style={{ flex: 1 }}>{t.label}</span>
                <span
                  style={pill(
                    mode === t.type ? t.color : "rgba(255,255,255,0.2)",
                    "transparent",
                    mode === t.type ? `${t.color}50` : "rgba(255,255,255,0.10)",
                  )}
                >
                  {t.key}
                </span>
              </button>
            ))}

            {/* Edit mode hint */}
            {isEdit && features.length > 0 && !editingFeatureId && (
              <div
                style={{
                  padding: "5px 8px",
                  fontSize: "9px",
                  color: `${EDIT_C}99`,
                  lineHeight: 1.6,
                  background: `${EDIT_C}0a`,
                  borderRadius: "6px",
                  margin: "2px 0",
                }}
              >
                Click a segment below to select · Then drag vertices
              </div>
            )}
            {isEdit && editingFeatureId && (
              <div
                style={{
                  padding: "5px 8px",
                  fontSize: "9px",
                  color: `${EDIT_C}cc`,
                  lineHeight: 1.6,
                  background: `${EDIT_C}14`,
                  border: `1px solid ${EDIT_C}30`,
                  borderRadius: "6px",
                  margin: "2px 0",
                }}
              >
                ✎ Drag vertices to edit · Click elsewhere to deselect
              </div>
            )}

            {/* Live result */}
            {/* {!isEdit && (liveResult || livePointsLength > 0) && (
              <div
                style={{
                  margin: "4px 0",
                  padding: "6px 8px",
                  borderRadius: "6px",
                  background: `${AMBER}10`,
                  border: `1px solid ${AMBER}30`,
                  fontSize: "11px",
                  color: AMBER,
                }}
              >
                {liveResult ? (
                  <>
                    <span style={{ opacity: 0.6, fontSize: "9px" }}>Live: </span>
                    {liveResult}
                  </>
                ) : (
                  <span style={{ opacity: 0.5, fontSize: "10px" }}>
                    {livePointsLength} pt{livePointsLength !== 1 ? "s" : ""} — dbl-click to finish
                  </span>
                )}
              </div>
            )}
            {!isEdit && mode !== "NONE" && livePointsLength === 0 && (
              <div style={{ padding: "5px 8px", fontSize: "9px", color: `${GREEN}80`, lineHeight: 1.6 }}>
                Click to add points · Dbl-click to finish
              </div>
            )} */}

            {/* Saved segments list */}
            {features.length > 0 && (
              <>
                <div
                  style={{
                    height: "1px",
                    background: "rgba(255,255,255,0.07)",
                    margin: "4px 0",
                  }}
                />
                <div
                  style={{
                    fontSize: "9px",
                    color: "rgba(255,255,255,0.3)",
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                    padding: "2px 4px 4px",
                  }}
                >
                  AOI
                </div>
                <div
                  style={{
                    maxHeight: "160px",
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: "2px",
                  }}
                >
                  {features.map((feat) =>
                  {
                    const isEditing = feat.id === editingFeatureId;
                    return (
                      <Tooltip
                        title={
                          feat.id !== activeFeatureId
                            ? t("Set active polygon")
                            : ""
                        }
                      >
                        <div
                          key={feat.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "7px",
                            padding: "6px 10px",
                            borderBottom: "1px solid rgba(255,255,255,0.04)",
                            background: isEditing
                              ? "rgba(255,146,43,0.08)"
                              : "transparent",
                            borderLeft: isEditing
                              ? "2px solid #ff922b"
                              : "2px solid transparent",
                            transition: "background 0.1s",
                            cursor:
                              feat.id === activeFeatureId
                                ? "default"
                                : "pointer",
                          }}
                          onClick={() =>
                          {
                            if (feat.id !== activeFeatureId)
                            {
                              dispatch(setActiveFeature({ id: feat.id }));
                            }
                          }}
                        >
                          {/* Color icon */}
                          <span
                            style={{
                              width: "16px",
                              height: "16px",
                              flexShrink: 0,
                              borderRadius: "3px",
                              background: feat.fill + "25",
                              border: `${feat.strokeWidth}px solid ${feat.stroke}`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "7px",
                              color: feat.fill,
                            }}
                          >
                            {TYPE_ICONS[feat.geometry.type]}
                          </span>

                          {/* Label */}
                          <span
                            style={{
                              flex: 1,
                              fontSize: "10px",
                              color: isEditing
                                ? "#ff922b"
                                : "rgba(255,255,255,0.65)",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {feat.label}
                          </span>

                          <button
                            // onClick={() =>
                            // {
                            //   dispatch(toggleFeatureVisibility({ id: feat.id }))
                            //   dispatch(setDrawingAOIMode('UPLOAD_AOI'))
                            // }}

                            onClick={(e) =>
                            {
                              e.stopPropagation();
                              dispatch(
                                toggleFeatureVisibility({ id: feat.id }),
                              );

                              if (activeFeatureId === feat.id) onStopEdit();
                            }}
                          >
                            {feat.visible ? (
                              <EyeOutlined style={{ fontSize: "11px" }} />
                            ) : (
                              <EyeInvisibleOutlined
                                style={{ fontSize: "11px" }}
                              />
                            )}
                          </button>

                          {/* Edit button */}
                          <button
                            onClick={() =>
                              isEditing ? onStopEdit() : onStartEdit(feat.id, feat)
                            }
                            title={isEditing ? "Stop editing" : "Edit vertices"}
                            style={{
                              background: isEditing
                                ? "rgba(255,146,43,0.18)"
                                : "transparent",
                              border: `1px solid ${isEditing ? "rgba(255,146,43,0.4)" : "transparent"}`,
                              borderRadius: "4px",
                              cursor: "pointer",
                              color: isEditing ? "#ff922b" : "white",
                              fontSize: "11px",
                              padding: "1px 5px",
                              lineHeight: 1,
                              outline: "none",
                              flexShrink: 0,
                              fontFamily: "inherit",
                              transition: "all 0.15s",
                            }}
                            onMouseEnter={(e) =>
                            {
                              if (!isEditing)
                              {
                                e.currentTarget.style.color = "#ff922b";
                                e.currentTarget.style.borderColor =
                                  "rgba(255,146,43,0.3)";
                              }
                            }}
                            onMouseLeave={(e) =>
                            {
                              if (!isEditing)
                              {
                                e.currentTarget.style.color = "white";
                                e.currentTarget.style.borderColor =
                                  "transparent";
                              }
                            }}
                          >
                            ✎
                          </button>

                          {/* Delete button */}
                          <button
                            onClick={() => onRemove(feat.id)}
                            title="Delete feature"
                            style={{
                              background: "transparent",
                              border: "none",
                              cursor: "pointer",
                              color: "rgba(255,80,80,0.40)",
                              fontSize: "12px",
                              padding: "0 2px",
                              lineHeight: 1,
                              outline: "none",
                              flexShrink: 0,
                              fontFamily: "inherit",
                              transition: "color 0.15s",
                            }}
                            onMouseEnter={(e) =>
                            {
                              e.currentTarget.style.color = "#ff6b6b";
                            }}
                            onMouseLeave={(e) =>
                            {
                              e.currentTarget.style.color =
                                "rgba(255,80,80,0.40)";
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      </Tooltip>
                    );
                  })}
                </div>
              </>
            )}

            {/* Clear all */}
            {hasData && (
              <>
                <div
                  style={{
                    height: "1px",
                    background: "rgba(255,255,255,0.07)",
                    margin: "4px 0",
                  }}
                />
                <button
                  onClick={() =>
                  {
                    onClear();
                    setOpen(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "6px 10px",
                    background: "transparent",
                    border: "1px solid transparent",
                    borderRadius: "8px",
                    cursor: "pointer",
                    color: "rgba(255,100,100,0.60)",
                    fontSize: "11px",
                    fontFamily: "inherit",
                    width: "100%",
                    textAlign: "left",
                    outline: "none",
                  }}
                  onMouseEnter={(e) =>
                  {
                    e.currentTarget.style.background = "rgba(255,80,80,0.08)";
                    e.currentTarget.style.color = "#ff6b6b";
                    e.currentTarget.style.borderColor = "rgba(255,80,80,0.25)";
                  }}
                  onMouseLeave={(e) =>
                  {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = "rgba(255,100,100,0.60)";
                    e.currentTarget.style.borderColor = "transparent";
                  }}
                >
                  <span>🗑</span>
                  <span>Clear all</span>
                </button>
              </>
            )}

            {shouldRenderUploadAOI && (
              <CommonComponents.ModalUI
                toggle={toggleUploadAOI}
                title={t("polygonType.uploadAOI")}
                open={openUploadAOI}
                width={"550px"}
                hiddenFooter
              >
                <FormUpload
                  toggleUpload={() =>
                  {
                    toggleUploadAOI();
                    dispatch(setDrawingAOIMode("NONE"));
                  }}
                />
              </CommonComponents.ModalUI>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AOIToolbar;
